import type { ExportJobDetail, ExportJobStatus } from "@/types/api";
import {
  defaultThisMonthRange,
  isIsoDateRangeInvalid,
} from "@/domain/invoices/invoiceFilterState";
import { parseIsoDate } from "@/utils/format/isoDate";

export type ExportListStatusFilter = "all" | "running" | "ready" | "failed";

export interface ExportListFilters {
  status: ExportListStatusFilter;
  dateFrom: string;
  dateTo: string;
}

export function defaultExportListFilters(): ExportListFilters {
  const { dateFrom, dateTo } = defaultThisMonthRange();
  return {
    status: "all",
    dateFrom,
    dateTo,
  };
}

/** Cleared toolbar state — no status or date restriction. */
export function clearedExportListFilters(): ExportListFilters {
  return {
    status: "all",
    dateFrom: "",
    dateTo: "",
  };
}

export function exportListFiltersEqual(
  a: ExportListFilters,
  b: ExportListFilters,
): boolean {
  return (
    a.status === b.status &&
    a.dateFrom === b.dateFrom &&
    a.dateTo === b.dateTo
  );
}

/** @deprecated use defaultExportListFilters() */
export const EMPTY_EXPORT_LIST_FILTERS: ExportListFilters =
  defaultExportListFilters();

export function parseExportListFiltersFromSearchParams(
  params: URLSearchParams,
): ExportListFilters {
  const { dateFrom: defFrom, dateTo: defTo } = defaultThisMonthRange();
  const hasDateFrom = params.has("date_from");
  const hasDateTo = params.has("date_to");

  return {
    status: (params.get("status") as ExportListStatusFilter) || "all",
    dateFrom: hasDateFrom ? (params.get("date_from")?.trim() ?? "") : defFrom,
    dateTo: hasDateTo ? (params.get("date_to")?.trim() ?? "") : defTo,
  };
}

export function validateExportListFilters(filters: ExportListFilters): string[] {
  const messages: string[] = [];
  if (filters.dateFrom && parseIsoDate(filters.dateFrom) === null) {
    messages.push("Export start date is not valid.");
  }
  if (filters.dateTo && parseIsoDate(filters.dateTo) === null) {
    messages.push("Export end date is not valid.");
  }
  if (isIsoDateRangeInvalid(filters.dateFrom, filters.dateTo)) {
    messages.push(
      "Export date: start must be on or before the end date.",
    );
  }
  return messages;
}

export function exportListDateFilterActive(
  filters: ExportListFilters,
): boolean {
  return validateExportListFilters(filters).length === 0;
}

export function serializeExportListFiltersToSearchParams(
  filters: ExportListFilters,
  base: URLSearchParams,
): URLSearchParams {
  const p = new URLSearchParams(base);

  p.delete("q");

  if (filters.status && filters.status !== "all") p.set("status", filters.status);
  else p.delete("status");

  p.set("date_from", filters.dateFrom);
  p.set("date_to", filters.dateTo);

  return p;
}

function matchesStatusFilter(
  status: ExportJobStatus,
  filter: ExportListStatusFilter,
): boolean {
  if (filter === "all") return true;
  if (filter === "running") return status === "PENDING" || status === "RUNNING";
  if (filter === "ready") return status === "SUCCESS";
  if (filter === "failed") return status === "FAILED";
  return true;
}

function dateInRange(iso: string, from: string, to: string): boolean {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return true;
  if (from) {
    const start = Date.parse(`${from}T00:00:00.000Z`);
    if (!Number.isNaN(start) && t < start) return false;
  }
  if (to) {
    const end = Date.parse(`${to}T23:59:59.999Z`);
    if (!Number.isNaN(end) && t > end) return false;
  }
  return true;
}

export function filterExportJobs(
  items: ExportJobDetail[],
  filters: ExportListFilters,
): ExportJobDetail[] {
  return items.filter((job) => {
    if (!matchesStatusFilter(job.status, filters.status)) return false;
    if (
      exportListDateFilterActive(filters) &&
      !dateInRange(job.created_at, filters.dateFrom, filters.dateTo)
    ) {
      return false;
    }
    return true;
  });
}
